import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button } from 'reactstrap';
import './style.scss';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import LoadingMessage from '../statusMessages/LoadingMessage';
import { strings } from '../../constants/localization';
import StepBullets from '../common/StepBullets/StepBullets';
import {
    addNewCompanyLocation,
    getPublicCompanyCategories,
    getChainLocations
} from '../../actions/companyChainActions';
import createValidationAcumulator from './ValidationAcumulator';
import { CompanyChainData } from './../interfaces';

interface AddCompanyLocationProps {
    parentCvr?: string;
    chainData: CompanyChainData;
    close: () => void;
    createNewUserHandler: () => void;
}

const AddCompanyLocation: React.FC<AddCompanyLocationProps> = ({ parentCvr, chainData, close, createNewUserHandler }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [state, setState] = useState({
        location: '',
        address: '',
        zipCode: '',
        city: '',
        cvr: '',
        phone: '',
        email: '',
        website: '',
        accountNo: '',
        registrationNo: '',
        selectedCategories: [],
        hideLocation: false,
    });
    const [allCategories, setAllCategories] = useState<{ value: string; label: string }[]>([]);
    const [Step1isValid, setStep1isValid] = useState<boolean>(true);
    const [Step2isValid, setStep2isValid] = useState<boolean>(true);
    const [Step3isValid, setStep3isValid] = useState<boolean>(true);
    const [Step1Clean, setStep1Clean] = useState<boolean>(true);
    const [Step2Clean, setStep2Clean] = useState<boolean>(true);
    const [Step3Clean, setStep3Clean] = useState<boolean>(true);
    const [Step1Valid] = useState(createValidationAcumulator());
    const [Step2Valid] = useState(createValidationAcumulator());
    const [Step3Valid] = useState(createValidationAcumulator());

    // Use the submitted data to set the state
    const handleChange = (event) => {
        if (Step3Valid.acknowledgeChange()) {
            setStep3Clean(true);
        }
        const { name, value } = event.target;
        setState((prevState) => ({
            ...prevState,
            [name]: value
        }));

        //jump focus input when is reached maxlength
        if (name === 'registrationNo' && value.length === 4) {
            document.getElementById('accountNo').focus();
        }
    };

    const getAllPublicCompanyCategories = () => {
        dispatch(getPublicCompanyCategories())
            .then((response) => {
                if (response.payload.status === 200) {
                    const cat = [];
                    response.payload.data.forEach((val) => {
                        cat.push({
                            value: val.id,
                            label: val.categoryName,
                        });
                    });
                    setLoading(false);
                    setAllCategories(cat);
                }
            })
            .catch((e) => {
                console.log(e);
                setLoading(false);
            });
    }

    const submitData = async () => {
        setLoading(true);
        let mapCategories = state.selectedCategories.map(
            (categ) => categ.label
        );
        const data = {
            address: state.address,
            cardNumber: state.accountNo ? state.accountNo : null,
            chainCompanyId: chainData.id,
            city: state.city,
            companyName: state.location,
            cvr: state.cvr.replace(/\s+/g, ''),
            email: state.email,
            phone: state.phone.replace(/\s+/g, ''),
            publicCompanyCategories: mapCategories,
            registrationNumber: state.registrationNo
                ? state.registrationNo
                : null,
            showInIndex: state.hideLocation,
            sorId: '',
            website: state.website,
            zipCode: state.zipCode,
            pNumber: '',
        };

        try {
            dispatch(addNewCompanyLocation(data)).then((response) => {
                dispatch(getChainLocations(chainData.id));
                if (response.payload.status === 200) {
                    return response.payload.data;
                }
            });
        } catch (err) {
            console.log('errorFromCompany', err);
            // Handle Error Here
            setLoading(false);
            return err;
        }
    }

    const nextStep = () => {
        let continueToNext = false;

        // check if the Step is valid, before continue to next
        switch (currentStep) {
            case 1: {
                continueToNext = Step1Valid.isValid();
                setStep1Clean(false);
                setStep1isValid(continueToNext);
                if (continueToNext) {
                    getAllPublicCompanyCategories();
                }
                break;
            }
            case 2: {
                continueToNext = Step2Valid.isValid();
                setStep2Clean(false);
                setStep2isValid(continueToNext);
                break;
            }
            case 3: {
                continueToNext = Step3Valid.isValid();
                setStep3Clean(false);
                setStep3isValid(continueToNext);
                break;
            }
            default:
                break;
        }

        // if continue is allowed, then update the current step, otherwise mark the step us unclean (has been visited)
        if (continueToNext) {
            // if we are allowed to continue and current step is 3, then submit data
            if (currentStep === 3) {
                submitData();
                setLoading(false);
                setCurrentStep(currentStep + 1);
            } else {
                setCurrentStep(currentStep >= 3 ? 4 : currentStep + 1);
            }
        }
    };

    const prevStep = () => {
        //reset step error message
        // setHasErrors(false);
        setCurrentStep(currentStep <= 1 ? 1 : currentStep - 1);

    };

    const previousButton = () => {
        // If the current step is not 1, then render the "previous" button
        if (currentStep > 1 && currentStep < 4) {
            return (
                <Button color='blue' onClick={prevStep}>
                    {strings.locationFlow_step2_back}
                </Button>
            );
        }
        return null;
    }

    const nextButton = () => {
        // If the current step is not 4, then render the "next" button
        if (currentStep < 4) {
            return (
                <Button color='blue' onClick={nextStep}>
                    {strings.locationFlow_step2_continue}
                </Button>
            );
        }
        if (currentStep === 4) {
            return (
                <Button color='blue' onClick={close}>
                    {strings.locationFlow_step4_close}
                </Button>
            );
        }
        return null;
    }

    const renderStepErrorMsg = () => {
        let stepErrorMsg;
        switch (currentStep) {
            case 1:
                if (!Step1Clean && !Step1Valid.isValid()) {
                    stepErrorMsg = strings.locationFlow_step1_requerdField;
                }
                break;
            case 2:
                if (!Step2Clean && !Step2Valid.isValid()) {
                    stepErrorMsg = strings.locationFlow_step2_error2;
                }
                break;
            case 3:
                if (
                    !Step3Valid.isChanged() &&
                    !Step3Clean &&
                    !Step3Valid.isValid()
                ) {
                    // verify if there was a selection made
                    if ('step3' in Step3Valid) {
                        stepErrorMsg = strings.locationFlow_step3_selectOption;
                        // then check the inputs validation
                    } else if (Object.keys(Step3Valid).length > 0) {
                        stepErrorMsg = strings.locationFlow_step3_enterNumber;
                    }
                }
                break;
            default:
                stepErrorMsg = undefined;
                break;
        }
        return stepErrorMsg ? (
            <div className='step_error showing'>{stepErrorMsg}</div>
        ) : (
            <div className='step_error not_showing'></div>
        );
    };

    // when the user switch the selection to the first one, then we need to reset the values
    const resetStep3Values = () => {
        setState((prevState) => ({
            ...prevState,
            registrationNo: '',
            accountNo: '',
        }));
    };

    return (
        <div id='addCompanyLocation'>
            {loading && <LoadingMessage />}
            {!loading && (
                <div className='content'>
                    <Step1
                        currentStep={currentStep}
                        handleChange={handleChange}
                        location={state.location}
                        address={state.address}
                        zipCode={state.zipCode}
                        city={state.city}
                        cvr={state.cvr}
                        parentCvr={parentCvr}
                        validator={Step1Valid}
                        cleanStep={Step1Clean}
                        stepIsValid={Step1isValid}
                    />
                    <Step2
                        currentStep={currentStep}
                        handleChange={handleChange}
                        phone={state.phone}
                        email={state.email}
                        website={state.website}
                        chainBrand={chainData.brand}
                        allCategories={allCategories}
                        selectedCategories={state.selectedCategories}
                        hideLocation={state.hideLocation}
                        validator={Step2Valid}
                        cleanStep={Step2Clean}
                        stepIsValid={Step2isValid}
                    />
                    <Step3
                        currentStep={currentStep}
                        handleChange={handleChange}
                        cvr={state.cvr}
                        registrationNo={state.registrationNo}
                        accountNo={state.accountNo}
                        chainBrand={chainData.brand}
                        validator={Step3Valid}
                        cleanStep={Step3Clean}
                        resetStep3Values={resetStep3Values}
                        stepIsValid={Step3isValid}
                    />
                    <Step4
                        currentStep={currentStep}
                        location={state.location}
                        address={state.address}
                        zipCode={state.zipCode}
                        city={state.city}
                        cvr={state.cvr}
                        phone={state.phone}
                        email={state.email}
                        website={state.website}
                        hideLocation={state.hideLocation}
                        registrationNo={state.registrationNo}
                        accountNo={state.accountNo}
                        createNewUserHandler={createNewUserHandler}
                    />

                    <div className='controls'>
                        {renderStepErrorMsg()}
                        <div className='actions-section'>
                            {previousButton()}
                            {nextButton()}
                        </div>
                        <StepBullets maxSteps={4} step={currentStep} />
                    </div>
                </div>
            )}
        </div>
    );


};

export default AddCompanyLocation;